import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { BLOQUES, GYM_WHATSAPP, formatRut, formatTelefono, rutNorm, getFechaHoy, getProximosDiasHabiles, formatFechaLarga, formatFechaCorta, fechaToISO, isoToFecha, API_URL } from '../constants';

export default function AlumnoApp({ alumnos, reservas: reservasAPI, entrenadores: entrenadoresProp, onNuevaReserva, onCancelarReserva }) {
  const entrenadores = useMemo(() => entrenadoresProp || [], [entrenadoresProp]);

  // ── Auth ──────────────────────────────────────────────────────────
  const [alumno, setAlumno]         = useState(null);
  const [rutInput, setRutInput]     = useState('');
  const [loginError, setLoginError] = useState('');

  // ── Navegación ────────────────────────────────────────────────────
  const [pantalla, setPantalla] = useState('login');

  // ── Reservas del alumno ───────────────────────────────────────────
  const [misReservas, setMisReservas]         = useState([]);
  const [reservaConfirmada, setReservaConfirmada] = useState(null);

  // ── Registro nuevo alumno ─────────────────────────────────────────
  // Sin selección de plan: quien se registra desde la app ya es socio
  // del gimnasio, solo está creando su acceso a la app.
  const FORM_REG_INIT = { rut: '', nombre: '', apellido: '', telefono: '', email: '' };
  const [formReg, setFormReg]   = useState(FORM_REG_INIT);
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [registrado, setRegistrado] = useState(null);

  const handleRegistro = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    setRegError('');
    try {
      const res = await axios.post(`${API_URL}/alumnos`, { ...formReg, origen: 'app', revisado: false });
      const nuevo = res.data;
      setAlumno(nuevo);
      setRegistrado(nuevo);
      setMisReservas([]);
      setFormReg(FORM_REG_INIT);
      setPantalla('registroOk');
    } catch (err) {
      setRegError('Error al registrar: ' + (err.response?.data?.error || err.message));
    } finally {
      setRegLoading(false);
    }
  };

  // Mensaje a recepción para que validen el autoregistro (evita registros
  // maliciosos sin que nadie del gimnasio se entere)
  const waLinkRegistro = (a) => {
    const msg = encodeURIComponent(
      `⚠️ Nuevo registro desde la App del Alumno:\n👤 ${a.nombre} ${a.apellido}\nRUT: ${a.rut}\nTeléfono: ${a.telefono || '—'}\n\nPor favor verifica que sea un alumno válido del gimnasio.`
    );
    return `https://wa.me/${GYM_WHATSAPP}?text=${msg}`;
  };

  // ── Formulario reserva ────────────────────────────────────────────
  const [paso, setPaso]       = useState(1);
  const [selEnt, setSelEnt]   = useState(null);
  const [selDia, setSelDia]   = useState(getFechaHoy);
  const [selBloque, setSelBloque] = useState(null);

  // ── Calendario mensual ────────────────────────────────────────────
  const [calMes, setCalMes] = useState(() => {
    const h = new Date(); return { y: h.getFullYear(), m: h.getMonth() };
  });
  const nombresMes = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const diasSemanaLabel = ['L','M','X','J','V','S','D'];
  const calMesAnterior = () => setCalMes(p => ({ y: p.m === 0 ? p.y - 1 : p.y, m: p.m === 0 ? 11 : p.m - 1 }));
  const calMesSiguiente = () => setCalMes(p => ({ y: p.m === 11 ? p.y + 1 : p.y, m: p.m === 11 ? 0 : p.m + 1 }));
  const calCeldas = useMemo(() => {
    const primerDia = new Date(calMes.y, calMes.m, 1);
    const totalDias = new Date(calMes.y, calMes.m + 1, 0).getDate();
    let offset = primerDia.getDay() - 1;
    if (offset < 0) offset = 6;
    const celdas = [];
    for (let i = 0; i < offset; i++) celdas.push(null);
    for (let d = 1; d <= totalDias; d++) celdas.push(d);
    return celdas;
  }, [calMes]);

  // ── Login ─────────────────────────────────────────────────────────
  const handleLogin = () => {
    const norm = rutNorm(rutInput);
    if (!norm) { setLoginError('Ingresa tu RUT.'); return; }
    const found = alumnos.find(a => rutNorm(a.rut) === norm);
    if (found) {
      setAlumno(found);
      const propias = (reservasAPI || []).filter(r => r.alumno_id === found.id);
      setMisReservas(propias.map(r => ({
        id: r.id,
        entId: r.profesor_id,
        entNombre: entrenadores.find(e => e.id === r.profesor_id)?.nombre || 'Entrenador',
        dia: r.fecha_reserva,
        bloque: r.hora_inicio?.slice(0, 5), // Postgres devuelve "HH:MM:SS", BLOQUES usa "HH:MM"
        alumno: `${found.nombre} ${found.apellido}`,
      })));
      setPantalla('home');
    } else {
      setLoginError('RUT no encontrado.');
    }
  };

  const handleLogout = () => {
    setAlumno(null); setRutInput(''); setLoginError('');
    setPantalla('login'); setMisReservas([]); resetReserva();
  };

  // ── Bloques ocupados ──────────────────────────────────────────────
  // reservasAPI covers all confirmed slots; only add optimistic local ones not yet confirmed
  const ocupados = useMemo(() => [
    ...(reservasAPI || []).map(r => ({ entId: r.profesor_id, dia: r.fecha_reserva, bloque: r.hora_inicio?.slice(0, 5) })),
    ...misReservas.filter(r => String(r.id).startsWith('local_')).map(r => ({ entId: r.entId, dia: r.dia, bloque: r.bloque })),
  ], [reservasAPI, misReservas]);

  const estaOcupado = (entId, dia, bloque) =>
    ocupados.some(o => o.entId === entId && o.dia === dia && o.bloque === bloque);

  // ── Entrenadores que puede elegir el alumno ────────────────────────
  // Si ya reservó con alguien antes, solo le aparece ese (evita que se
  // llene la agenda de un mismo alumno con todos los PT). Si es su primera
  // reserva, solo puede elegir entre los marcados como "alumno nuevo".
  const entrenadoresDisponibles = useMemo(() => {
    const yaAsignados = [...new Set(misReservas.map(r => r.entId))];
    if (yaAsignados.length > 0) return entrenadores.filter(e => yaAsignados.includes(e.id));
    return entrenadores.filter(e => e.intake);
  }, [entrenadores, misReservas]);

  // ── Crear reserva ─────────────────────────────────────────────────
  const handleConfirmarReserva = () => {
    if (!selEnt || !selDia || !selBloque) return;
    const ent = entrenadores.find(e => e.id === selEnt);
    const nueva = {
      id: `local_${Date.now()}`,
      entId: selEnt,
      entNombre: ent?.nombre || '',
      entColor: ent?.color,
      dia: selDia,
      bloque: selBloque,
      alumno: `${alumno.nombre} ${alumno.apellido}`,
    };
    setMisReservas(prev => [...prev, nueva]);
    setReservaConfirmada(nueva);
    onNuevaReserva && onNuevaReserva({
      profesor_id: selEnt,
      alumno_id: alumno.id,
      alumno_nombre: `${alumno.nombre} ${alumno.apellido}`,
      fecha_reserva: selDia,
      hora_inicio: selBloque,
      hora_fin: BLOQUES[BLOQUES.indexOf(selBloque) + 1] || '22:00',
      tipo_sesion: 'Personalizado',
    });
    resetReserva();
    setPantalla('confirmacion');
  };

  const resetReserva = () => { setPaso(1); setSelEnt(null); setSelDia(getFechaHoy()); setSelBloque(null); };

  // ── Cancelar reserva ──────────────────────────────────────────────
  const handleCancelar = (id) => {
    setMisReservas(prev => prev.filter(r => r.id !== id));
    if (!String(id).startsWith('local_')) onCancelarReserva && onCancelarReserva(id);
  };

  // ── WhatsApp link ─────────────────────────────────────────────────
  // Si el entrenador tiene su propio número → le avisa directo a él
  // Si no → va a recepción
  const waLink = (r) => {
    const ent = entrenadores.find(e => e.id === r.entId);
    const destino = ent?.whatsapp || GYM_WHATSAPP;
    const esEntrenador = !!ent?.whatsapp;

    const msg = esEntrenador
      ? encodeURIComponent(
          `Hola ${r.entNombre}! 🏋️\nTienes una nueva reserva de PT:\n👤 Alumno: ${alumno?.nombre} ${alumno?.apellido}\n📅 Día: ${formatFechaLarga(r.dia)}\n⏰ Hora: ${r.bloque}\n\nRUT: ${alumno?.rut}\n¡Te esperamos! — Infinity Center ✅`
        )
      : encodeURIComponent(
          `Hola Infinity Center! 🏋️\nAcabo de reservar una sesión de PT:\n👤 Entrenador: ${r.entNombre}\n📅 Día: ${formatFechaLarga(r.dia)}\n⏰ Hora: ${r.bloque}\n\nSoy ${alumno?.nombre} ${alumno?.apellido}, RUT ${alumno?.rut}.\n¿Me pueden confirmar? ✅`
        );

    return `https://wa.me/${destino}?text=${msg}`;
  };

  const estadoColor = alumno
    ? alumno.dias_vigentes > 5 ? 'var(--green)' : alumno.dias_vigentes > 0 ? 'var(--cyan)' : 'var(--red)'
    : 'var(--muted)';

  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════

  if (pantalla === 'login') return (
    <div className="ar-root">
      <div className="ar-login-card">
        <div className="ar-login-logo">∞ INFINITY <span>CENTER</span></div>
        <div className="ar-login-sub">App del Alumno</div>
        <div className="ar-login-icon">🏋️</div>
        <div className="ar-login-title">Inicia sesión</div>
        <div className="ar-login-hint">Ingresa tu RUT para ver tus reservas y agendar con un PT</div>
        <div className="ar-login-field">
          <label className="ar-label">Tu RUT</label>
          <input className="ar-input" placeholder="12.345.678-9" value={rutInput} maxLength={12}
            onChange={e => { setRutInput(formatRut(e.target.value)); setLoginError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        </div>
        {loginError && (
          <div className="ar-error">
            {loginError}{' '}
            <button className="ar-link-btn" onClick={() => {
              setFormReg(f => ({ ...f, rut: rutInput }));
              setRegError('');
              setPantalla('registro');
            }}>¿Eres nuevo? Regístrate aquí →</button>
          </div>
        )}
        <button className="ar-btn-primary" onClick={handleLogin}>Ingresar →</button>
        <div className="ar-login-divider">¿Primera vez en el gym?</div>
        <button className="ar-btn-secondary" onClick={() => {
          setFormReg(f => ({ ...f, rut: rutInput }));
          setRegError('');
          setPantalla('registro');
        }}>➕ Registrarme como alumno nuevo</button>
      </div>
    </div>
  );

  if (pantalla === 'registro') return (
    <div className="ar-root">
      <div className="ar-login-card" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="ar-back" style={{ alignSelf: 'flex-start', marginBottom: 4 }}
          onClick={() => setPantalla('login')}>← Volver</button>
        <div className="ar-login-logo">∞ INFINITY <span>CENTER</span></div>
        <div className="ar-login-title" style={{ fontSize: 22 }}>Registro de alumno</div>
        <form onSubmit={handleRegistro} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
          <div className="ar-login-field">
            <label className="ar-label">RUT *</label>
            <input className="ar-input" placeholder="12.345.678-9" value={formReg.rut} maxLength={12} required
              onChange={e => setFormReg(f => ({ ...f, rut: formatRut(e.target.value) }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="ar-login-field">
              <label className="ar-label">Nombre *</label>
              <input className="ar-input" style={{ fontSize: 15, letterSpacing: 0 }} placeholder="Juan" value={formReg.nombre} required
                onChange={e => setFormReg(f => ({ ...f, nombre: e.target.value }))} />
            </div>
            <div className="ar-login-field">
              <label className="ar-label">Apellido *</label>
              <input className="ar-input" style={{ fontSize: 15, letterSpacing: 0 }} placeholder="Pérez" value={formReg.apellido} required
                onChange={e => setFormReg(f => ({ ...f, apellido: e.target.value }))} />
            </div>
          </div>
          <div className="ar-login-field">
            <label className="ar-label">Teléfono *</label>
            <input className="ar-input" style={{ fontSize: 15, letterSpacing: 0 }} placeholder="+56 9 1234 5678" value={formReg.telefono} required
              onChange={e => setFormReg(f => ({ ...f, telefono: formatTelefono(e.target.value) }))} />
          </div>
          <div className="ar-login-field">
            <label className="ar-label">Email <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(opcional)</span></label>
            <input className="ar-input" style={{ fontSize: 15, letterSpacing: 0 }} placeholder="correo@ejemplo.com" type="email" value={formReg.email}
              onChange={e => setFormReg(f => ({ ...f, email: e.target.value }))} />
          </div>
          {regError && <div className="ar-error">{regError}</div>}
          <button type="submit" className="ar-btn-primary" disabled={regLoading} style={{ marginTop: 4 }}>
            {regLoading ? 'Registrando...' : '✓ Completar Registro'}
          </button>
        </form>
      </div>
    </div>
  );

  if (pantalla === 'registroOk' && registrado) return (
    <div className="ar-root">
      <div className="ar-login-card">
        <div className="ar-confirm-icon">✓</div>
        <div className="ar-confirm-title">¡Registro recibido!</div>
        <div className="ar-resumen-card">
          {[['Nombre', `${registrado.nombre} ${registrado.apellido}`], ['RUT', registrado.rut]].map(([k,v]) => (
            <div key={k} className="ar-resumen-row"><span>{k}</span><strong>{v}</strong></div>
          ))}
        </div>
        <a className="ar-btn-wa" href={waLinkRegistro(registrado)} target="_blank" rel="noreferrer">
          💬 Avisar a recepción
        </a>
        <div className="ar-confirm-wa-hint">Por seguridad, recepción valida cada registro nuevo hecho desde la app</div>
        <button className="ar-btn-secondary" onClick={() => setPantalla('home')}>Continuar →</button>
      </div>
    </div>
  );

  if (pantalla === 'confirmacion' && reservaConfirmada) return (
    <div className="ar-root">
      <div className="ar-login-card">
        <div className="ar-confirm-icon">✓</div>
        <div className="ar-confirm-title">¡Reserva confirmada!</div>
        <div className="ar-resumen-card">
          {[['Entrenador', reservaConfirmada.entNombre], ['Día', formatFechaLarga(reservaConfirmada.dia)], ['Hora', reservaConfirmada.bloque], ['Alumno', reservaConfirmada.alumno]].map(([k,v]) => (
            <div key={k} className="ar-resumen-row"><span>{k}</span><strong>{v}</strong></div>
          ))}
        </div>
        <a className="ar-btn-wa" href={waLink(reservaConfirmada)} target="_blank" rel="noreferrer">
          💬 Confirmar por WhatsApp
        </a>
        <div className="ar-confirm-wa-hint">Se abrirá WhatsApp con el mensaje listo para enviar al gimnasio</div>
        <button className="ar-btn-secondary" onClick={() => setPantalla('home')}>← Volver al inicio</button>
      </div>
    </div>
  );

  return (
    <div className="ar-root">
      {/* Top bar */}
      <div className="ar-topbar">
        <div className="ar-topbar-info">
          <div className="ar-topbar-avatar">{alumno?.nombre?.[0]}{alumno?.apellido?.[0]}</div>
          <div>
            <div className="ar-topbar-name">{alumno?.nombre} {alumno?.apellido}</div>
            <div className="ar-topbar-plan" style={{ color: estadoColor }}>
              {alumno?.plan} · {alumno?.dias_vigentes > 0 ? `${alumno.dias_vigentes} días` : 'Vencida'}
            </div>
          </div>
        </div>
        <button className="ar-logout-btn" onClick={handleLogout}>Salir</button>
      </div>

      <div className="ar-content">

        {/* HOME */}
        {pantalla === 'home' && (
          <div className="ar-screen">
            <div className="ar-welcome">Hola, <span>{alumno?.nombre}!</span> 👋</div>
            {alumno?.dias_vigentes <= 0 && (
              <div className="ar-alert" style={{ borderColor: 'var(--red)', background: 'rgba(255,59,59,.08)' }}>
                ⚠️ Tu membresía está vencida. Renueva en el tótem o en recepción.
              </div>
            )}
            {alumno?.dias_vigentes > 0 && alumno?.dias_vigentes <= 5 && (
              <div className="ar-alert" style={{ borderColor: 'var(--cyan)', background: 'rgba(0,200,232,.08)' }}>
                ⏰ Tu membresía vence en {alumno.dias_vigentes} días. ¡Renueva pronto!
              </div>
            )}
            <div className="ar-home-grid">
              {[
                { id: 'reservar',    icon: '📅', label: 'Reservar PT',   sub: 'Agenda con un entrenador' },
                { id: 'misReservas', icon: '📋', label: 'Mis Reservas',  sub: `${misReservas.length} sesión${misReservas.length !== 1 ? 'es' : ''}` },
                { id: 'perfil',      icon: '👤', label: 'Mi Perfil',     sub: 'Ver mi membresía' },
                { id: '_wa',         icon: '💬', label: 'Contacto',      sub: 'WhatsApp del gym' },
              ].map(b => (
                <button key={b.id} className="ar-home-btn"
                  onClick={() => b.id === '_wa' ? window.open(`https://wa.me/${GYM_WHATSAPP}`, '_blank') : (b.id === 'reservar' ? (resetReserva(), setPantalla('reservar')) : setPantalla(b.id))}>
                  <span className="ar-home-icon">{b.icon}</span>
                  <span className="ar-home-label">{b.label}</span>
                  <span className="ar-home-sub">{b.sub}</span>
                </button>
              ))}
            </div>
            {misReservas.length > 0 && (
              <div className="ar-section">
                <div className="ar-section-title">Próximas sesiones</div>
                {misReservas.slice(0, 3).map(r => {
                  const ent = entrenadores.find(e => e.id === r.entId);
                  return (
                    <div key={r.id} className="ar-sesion-card">
                      <div className="ar-sesion-dot" style={{ background: ent?.color || 'var(--cyan)' }} />
                      <div className="ar-sesion-info">
                        <div className="ar-sesion-ent">{r.entNombre}</div>
                        <div className="ar-sesion-tiempo">{formatFechaCorta(r.dia)} · {r.bloque}</div>
                      </div>
                      <div className="ar-sesion-badge">PT</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* RESERVAR */}
        {pantalla === 'reservar' && (
          <div className="ar-screen">
            <div className="ar-screen-header">
              <button className="ar-back" onClick={() => { resetReserva(); setPantalla('home'); }}>←</button>
              <div className="ar-screen-title">Reservar PT</div>
            </div>
            <div className="ar-pasos">
              {['Entrenador','Día','Horario','Confirmar'].map((p, i) => (
                <div key={p} className={`ar-paso ${paso === i+1 ? 'active' : paso > i+1 ? 'done' : ''}`}>
                  <div className="ar-paso-num">{paso > i+1 ? '✓' : i+1}</div>
                  <div className="ar-paso-label">{p}</div>
                </div>
              ))}
            </div>

            {paso === 1 && entrenadoresDisponibles.length === 0 && (
              <div className="ar-empty">
                <div style={{ fontSize: 40 }}>🏋️</div>
                <div>Aún no hay entrenadores disponibles para reservar. Vuelve más tarde.</div>
              </div>
            )}

            {paso === 1 && entrenadoresDisponibles.length > 0 && (
              <div>
                <div className="ar-step-hint">
                  {misReservas.length > 0 ? 'Tu entrenador asignado' : '¿Con quién quieres entrenar?'}
                </div>
                <div className="ar-ent-grid">
                  {entrenadoresDisponibles.map(e => (
                    <button key={e.id} className={`ar-ent-card ${selEnt === e.id ? 'selected' : ''}`}
                      style={{ borderColor: selEnt === e.id ? e.color : 'var(--border)', background: selEnt === e.id ? e.colorBg : 'var(--bg3)' }}
                      onClick={() => setSelEnt(e.id)}>
                      <div className="ar-ent-av" style={{ background: e.color }}>{e.nombre[0]}</div>
                      <div className="ar-ent-nombre">{e.nombre}</div>
                    </button>
                  ))}
                </div>
                <button className="ar-btn-primary" disabled={!selEnt} onClick={() => setPaso(2)}>Siguiente →</button>
              </div>
            )}

            {paso === 2 && (
              <div>
                <div className="ar-step-hint">¿Qué día prefieres?</div>
                <div className="ar-cal-wrap">
                  <div className="ar-cal-header">
                    <button className="ar-cal-nav" onClick={calMesAnterior}>‹</button>
                    <span className="ar-cal-titulo">{nombresMes[calMes.m]} {calMes.y}</span>
                    <button className="ar-cal-nav" onClick={calMesSiguiente}>›</button>
                  </div>
                  <div className="ar-cal-grid">
                    {diasSemanaLabel.map(d => (
                      <div key={d} className="ar-cal-dow">{d}</div>
                    ))}
                    {calCeldas.map((d, i) => {
                      if (!d) return <div key={`e${i}`} />;
                      const fecha = fechaToISO(new Date(calMes.y, calMes.m, d));
                      const dow = new Date(calMes.y, calMes.m, d).getDay();
                      const hoyISO = getFechaHoy();
                      const disabled = dow === 0 || dow === 6 || fecha < hoyISO;
                      return (
                        <button
                          key={fecha}
                          disabled={disabled}
                          onClick={() => setSelDia(fecha)}
                          className={`ar-cal-dia${selDia === fecha ? ' selected' : ''}${fecha === hoyISO ? ' hoy' : ''}${disabled ? ' disabled' : ''}`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  {selDia && (
                    <div className="ar-cal-seleccion">📅 {formatFechaLarga(selDia)}</div>
                  )}
                </div>
                <div className="ar-nav-btns">
                  <button className="ar-btn-secondary" onClick={() => setPaso(1)}>← Atrás</button>
                  <button className="ar-btn-primary" onClick={() => setPaso(3)}>Siguiente →</button>
                </div>
              </div>
            )}

            {paso === 3 && (
              <div>
                <div className="ar-step-hint">Elige el horario</div>
                <div className="ar-bloques-grid">
                  {BLOQUES.map(b => {
                    const ocupado = estaOcupado(selEnt, selDia, b);
                    return (
                      <button key={b} className={`ar-bloque-btn ${selBloque === b ? 'active' : ''} ${ocupado ? 'ocupado' : ''}`}
                        disabled={ocupado} onClick={() => setSelBloque(b)}>
                        {b}{ocupado && <span className="ar-bloque-x"> ✗</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="ar-nav-btns">
                  <button className="ar-btn-secondary" onClick={() => setPaso(2)}>← Atrás</button>
                  <button className="ar-btn-primary" disabled={!selBloque} onClick={() => setPaso(4)}>Siguiente →</button>
                </div>
              </div>
            )}

            {paso === 4 && (() => {
              const ent = entrenadores.find(e => e.id === selEnt);
              return (
                <div>
                  <div className="ar-step-hint">Confirma tu reserva</div>
                  <div className="ar-resumen-card">
                    {[['Entrenador', ent?.nombre], ['Día', formatFechaLarga(selDia)], ['Hora', selBloque], ['Tipo', 'Entrenamiento Personal']].map(([k,v]) => (
                      <div key={k} className="ar-resumen-row"><span>{k}</span><strong style={{ color: k==='Entrenador' ? ent?.color : 'var(--text)' }}>{v}</strong></div>
                    ))}
                  </div>
                  <div className="ar-nav-btns">
                    <button className="ar-btn-secondary" onClick={() => setPaso(3)}>← Atrás</button>
                    <button className="ar-btn-primary" onClick={handleConfirmarReserva}>✓ Confirmar</button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* MIS RESERVAS */}
        {pantalla === 'misReservas' && (
          <div className="ar-screen">
            <div className="ar-screen-header">
              <button className="ar-back" onClick={() => setPantalla('home')}>←</button>
              <div className="ar-screen-title">Mis Reservas</div>
            </div>
            {misReservas.length === 0
              ? <div className="ar-empty"><div style={{ fontSize: 40 }}>📅</div><div>No tienes reservas aún</div>
                  <button className="ar-btn-primary" style={{ marginTop: 16 }} onClick={() => { resetReserva(); setPantalla('reservar'); }}>Reservar ahora</button>
                </div>
              : misReservas.map(r => {
                  const ent = entrenadores.find(e => e.id === r.entId);
                  return (
                    <div key={r.id} className="ar-reserva-card" style={{ borderLeft: `4px solid ${ent?.color || 'var(--cyan)'}` }}>
                      <div className="ar-reserva-top">
                        <div>
                          <div className="ar-reserva-ent" style={{ color: ent?.color }}>{r.entNombre}</div>
                          <div className="ar-reserva-tiempo">{formatFechaCorta(r.dia)} · {r.bloque}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <a className="ar-wa-mini" href={waLink(r)} target="_blank" rel="noreferrer">💬</a>
                          <button className="ar-cancel-btn" onClick={() => handleCancelar(r.id)}>✕</button>
                        </div>
                      </div>
                    </div>
                  );
                })
            }
            <button className="ar-btn-primary" style={{ marginTop: 12 }} onClick={() => { resetReserva(); setPantalla('reservar'); }}>+ Nueva reserva</button>
          </div>
        )}

        {/* PERFIL */}
        {pantalla === 'perfil' && (
          <div className="ar-screen">
            <div className="ar-screen-header">
              <button className="ar-back" onClick={() => setPantalla('home')}>←</button>
              <div className="ar-screen-title">Mi Perfil</div>
            </div>
            <div className="ar-perfil-avatar">{alumno?.nombre?.[0]}{alumno?.apellido?.[0]}</div>
            <div className="ar-perfil-name">{alumno?.nombre} {alumno?.apellido}</div>
            <div className="ar-perfil-rut">{alumno?.rut}</div>
            <div className="ar-perfil-card">
              {[['Plan', alumno?.plan], ['Estado', alumno?.dias_vigentes > 0 ? 'Activo ✓' : 'Vencido ✗'], ['Días restantes', alumno?.dias_vigentes], ['Vence el', alumno?.fecha_vencimiento]].map(([k,v]) => (
                <div key={k} className="ar-perfil-row">
                  <span>{k}</span>
                  <strong style={{ color: k==='Estado' ? estadoColor : 'var(--text)' }}>{v}</strong>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="ar-bottomnav">
        {[
          { id: 'home',        icon: '🏠', label: 'Inicio'   },
          { id: 'reservar',    icon: '📅', label: 'Reservar' },
          { id: 'misReservas', icon: '📋', label: 'Mis PT'   },
          { id: 'perfil',      icon: '👤', label: 'Perfil'   },
        ].map(tab => (
          <button key={tab.id} className={`ar-nav-tab ${pantalla === tab.id ? 'active' : ''}`}
            onClick={() => { if (tab.id === 'reservar') resetReserva(); setPantalla(tab.id); }}>
            <span className="ar-nav-icon">{tab.icon}</span>
            <span className="ar-nav-label">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

